// ========================================
// BUSINESS INFO SERVICE
// Service to retrieve business information from registration data
// ========================================

import { kvGet, kvSet, kvRemove } from '../infrastructure/storage/kvAccess';
import { publishBusinessLogoUpdated } from '../infrastructure/events/legacyEventBridge';
import { shouldAllowSupabaseNetwork } from '../utils/supabaseReachability';
import { getCurrentBusinessId } from '../utils/sharedCore';

class BusinessInfoService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 30 * 1000; // 30 seconds cache (shorter for more frequent updates)
  }

  /**
   * Get business information from localStorage or Supabase
   */
  async getBusinessInfo(businessId = null) {
    try {
      // Try to get from cache first
      const cacheKey = businessId || 'current';
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }

      let businessInfo = null;
      let businessInfoFromStorage = null;

      // First try to get from localStorage (for offline/registered businesses) - but don't use it yet
      try {
        const storedBusiness = kvGet('currentBusiness');
        if (storedBusiness) {
          // Handle corrupted data - check for "[object Object]" string
          const isCorrupted = storedBusiness === '[object Object]' || 
                             storedBusiness === '"[object Object]"' || 
                             (typeof storedBusiness === 'string' && storedBusiness.trim() === '');
          
          if (isCorrupted) {
            console.warn('⚠️ [BUSINESS INFO] Corrupted currentBusiness data detected, cleaning up');
            kvRemove('currentBusiness');
          } else {
            let parsed;
            // Check if it's already an object (shouldn't happen, but handle it)
            if (typeof storedBusiness === 'object' && storedBusiness !== null) {
              parsed = storedBusiness;
            } else if (typeof storedBusiness === 'string') {
              try {
                parsed = JSON.parse(storedBusiness);
              } catch (parseError) {
                console.warn('⚠️ [BUSINESS INFO] Failed to parse currentBusiness, removing corrupted data:', parseError);
                kvRemove('currentBusiness');
                parsed = null;
              }
            } else {
              parsed = null;
            }
            
            if (parsed && typeof parsed === 'object') {
              businessInfoFromStorage = {
                id: parsed.id || parsed.businessId,
                name: parsed.business_name || parsed.name || 'Entel',
                address: parsed.address || 'Adresse du magasin',
                phone: parsed.phone || 'Téléphone',
                email: parsed.email || 'Email',
                logo: parsed.logo || null,
                owner_name: parsed.owner_name || 'Propriétaire',
                nif: parsed.nif || 'NIF',
                business_type: parsed.business_type || 'magasin',
                status: parsed.status || 'active'
              };
            }
          }
        }
      } catch (error) {
        console.warn('Error reading business info from localStorage:', error);
      }

      if (shouldAllowSupabaseNetwork() && typeof window !== 'undefined') {
        try {
          const { supabase } = await import('../config/supabase');
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data, error } = await supabase
              .from('businesses')
              .select('*')
              .eq('auth_user_id', user.id)
              .single();

            if (!error && data) {
              // Handle logo: if it's a storage path, get the public URL
              let logoUrl = data.logo || null;
              
              // If logo is a storage path (starts with 'business-assets/'), get public URL
              if (logoUrl && typeof logoUrl === 'string') {
                // Check if it's already a full URL (http/https)
                const isFullUrl = logoUrl.startsWith('http://') || logoUrl.startsWith('https://');
                
                if (!isFullUrl && logoUrl.startsWith('business-assets/')) {
                  try {
                    const { data: urlData } = supabase.storage
                      .from('business-assets')
                      .getPublicUrl(logoUrl);
                    
                    if (urlData?.publicUrl) {
                      logoUrl = urlData.publicUrl;
                    }
                  } catch (urlError) {
                    console.warn('Error getting public URL for logo:', urlError);
                    // Keep original logo value if URL fetch fails
                  }
                }
                // If it's already a full URL or base64, use it as is
              }
              
              businessInfo = {
                id: data.id,
                name: data.business_name || data.name || 'Entel',
                address: data.address || 'Adresse du magasin',
                phone: data.phone || 'Téléphone',
                email: data.email || 'Email',
                logo: logoUrl, // Use the processed logo (URL or base64)
                owner_name: data.owner_name || 'Propriétaire',
                nif: data.nif || 'NIF',
                business_type: data.business_type || 'magasin',
                status: data.status || 'active'
              };
              
              // Update localStorage with Supabase data to keep it in sync
              try {
                const storedBusiness = kvGet('currentBusiness');
                if (storedBusiness) {
                  // Handle corrupted data
                  const isCorrupted = storedBusiness === '[object Object]' || 
                                     storedBusiness === '"[object Object]"' || 
                                     (typeof storedBusiness === 'string' && storedBusiness.trim() === '');
                  
                  if (isCorrupted) {
                    // Create new object if data is corrupted
                    const newBusiness = {
                      id: businessInfo.id,
                      business_name: businessInfo.name,
                      name: businessInfo.name,
                      address: businessInfo.address,
                      phone: businessInfo.phone,
                      email: businessInfo.email,
                      logo: businessInfo.logo,
                      owner_name: businessInfo.owner_name,
                      nif: businessInfo.nif,
                      business_type: businessInfo.business_type,
                      status: businessInfo.status
                    };
                    kvSet('currentBusiness', newBusiness);
                  } else {
                    let parsed;
                    // Check if it's already an object
                    if (typeof storedBusiness === 'object' && storedBusiness !== null) {
                      parsed = storedBusiness;
                    } else if (typeof storedBusiness === 'string') {
                      try {
                        parsed = JSON.parse(storedBusiness);
                      } catch (parseError) {
                        // If parsing fails, create new object
                        parsed = {
                          id: businessInfo.id,
                          business_name: businessInfo.name,
                          name: businessInfo.name
                        };
                      }
                    } else {
                      parsed = { id: businessInfo.id, business_name: businessInfo.name, name: businessInfo.name };
                    }
                    
                    if (parsed && typeof parsed === 'object') {
                      parsed.business_name = businessInfo.name;
                      parsed.name = businessInfo.name;
                      parsed.address = businessInfo.address;
                      parsed.phone = businessInfo.phone;
                      parsed.email = businessInfo.email;
                      parsed.logo = businessInfo.logo;
                      parsed.owner_name = businessInfo.owner_name;
                      parsed.nif = businessInfo.nif;
                      kvSet('currentBusiness', parsed);
                    }
                  }
                } else {
                  // No existing data, create new
                  const newBusiness = {
                    id: businessInfo.id,
                    business_name: businessInfo.name,
                    name: businessInfo.name,
                    address: businessInfo.address,
                    phone: businessInfo.phone,
                    email: businessInfo.email,
                    logo: businessInfo.logo,
                    owner_name: businessInfo.owner_name,
                    nif: businessInfo.nif,
                    business_type: businessInfo.business_type,
                    status: businessInfo.status
                  };
                  kvSet('currentBusiness', newBusiness);
                }
              } catch (error) {
                console.warn('Error updating localStorage with Supabase data:', error);
              }
            }
          }
        } catch (error) {
          console.warn('Error fetching business info from Supabase:', error);
        }
      }

      // If Supabase didn't return data, use localStorage data
      if (!businessInfo && businessInfoFromStorage) {
        businessInfo = businessInfoFromStorage;
      }

      // Fallback to default values
      if (!businessInfo) {
        businessInfo = {
          id: 'default',
          name: 'Entel',
          address: 'Adresse du magasin',
          phone: 'Téléphone',
          email: 'Email',
          logo: null,
          owner_name: 'Propriétaire',
          nif: 'NIF',
          business_type: 'magasin',
          status: 'active'
        };
      }

      // Cache the result
      this.cache.set(cacheKey, {
        data: businessInfo,
        timestamp: Date.now()
      });

      return businessInfo;
    } catch (error) {
      console.error('Error getting business info:', error);
      return {
        id: 'default',
        name: 'Entel',
        address: 'Adresse du magasin',
        phone: 'Téléphone',
        email: 'Email',
        logo: null,
        owner_name: 'Propriétaire',
        nif: 'NIF',
        business_type: 'magasin',
        status: 'active'
      };
    }
  }

  /**
   * Get business logo as base64 string
   */
  async getBusinessLogo(businessId = null) {
    try {
      const businessInfo = await this.getBusinessInfo(businessId);
      return businessInfo.logo;
    } catch (error) {
      console.error('Error getting business logo:', error);
      return null;
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Force refresh business info from Supabase (bypass cache)
   * @param {string} businessId - The business ID
   * @returns {Promise<Object>} Business info
   */
  async refreshBusinessInfo(businessId = null) {
    // Clear cache first
    this.clearCache();
    // Then get fresh data
    return await this.getBusinessInfo(businessId);
  }

  /**
   * Update business info in localStorage
   */
  updateBusinessInfo(businessData) {
    try {
      kvSet('currentBusiness', businessData);
      this.clearCache(); // Clear cache to force refresh
    } catch (error) {
      console.error('Error updating business info:', error);
    }
  }

  /**
   * Upload logo to Supabase Storage and update business info
   * @param {File} file - The logo file to upload
   * @param {string} businessId - The business ID
   * @returns {Promise<{success: boolean, logoUrl?: string, error?: string}>}
   */
  async uploadLogo(file, businessId = null) {
    try {
      // Validate file
      if (!file || !file.type.startsWith('image/')) {
        return { success: false, error: 'Invalid file type. Please upload an image.' };
      }

      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        return { success: false, error: 'File size too large. Maximum size is 2MB.' };
      }

      // Convert file to base64 for local storage
      const base64Logo = await this.fileToBase64(file);

      // Get current business ID
      const currentBusinessId = businessId || this.getCurrentBusinessId();

      // Update local storage immediately - update both currentBusiness and currentUser.business
      try {
        // Update currentBusiness
        const storedBusiness = kvGet('currentBusiness');
        if (storedBusiness) {
          // Handle corrupted data
          const isCorrupted = storedBusiness === '[object Object]' || 
                             storedBusiness === '"[object Object]"' || 
                             (typeof storedBusiness === 'string' && storedBusiness.trim() === '');
          
          if (isCorrupted) {
            console.warn('⚠️ [LOGO] Corrupted currentBusiness data, creating new entry');
            const newBusiness = { id: currentBusinessId, logo: base64Logo };
            kvSet('currentBusiness', newBusiness);
          } else {
            let business;
            if (typeof storedBusiness === 'object' && storedBusiness !== null) {
              business = storedBusiness;
            } else if (typeof storedBusiness === 'string') {
              try {
                business = JSON.parse(storedBusiness);
              } catch (parseError) {
                console.warn('⚠️ [LOGO] Failed to parse currentBusiness, creating new entry:', parseError);
                business = { id: currentBusinessId, logo: base64Logo };
              }
            } else {
              business = { id: currentBusinessId, logo: base64Logo };
            }
            
            if (business && typeof business === 'object') {
              business.logo = base64Logo;
              kvSet('currentBusiness', business);
            }
          }
        } else {
          // No existing data, create new
          const newBusiness = { id: currentBusinessId, logo: base64Logo };
          kvSet('currentBusiness', newBusiness);
        }
        
        // Also update currentUser.business to ensure Layout1 can see it
        const currentUser = kvGet('currentUser');
        if (currentUser) {
          // Handle corrupted data
          const isUserCorrupted = currentUser === '[object Object]' || 
                                  currentUser === '"[object Object]"' || 
                                  (typeof currentUser === 'string' && currentUser.trim() === '');
          
          if (isUserCorrupted) {
            console.warn('⚠️ [LOGO] Corrupted currentUser data, skipping update');
          } else {
            let user;
            if (typeof currentUser === 'object' && currentUser !== null) {
              user = currentUser;
            } else if (typeof currentUser === 'string') {
              try {
                user = JSON.parse(currentUser);
              } catch (parseError) {
                console.warn('⚠️ [LOGO] Failed to parse currentUser:', parseError);
                user = null;
              }
            } else {
              user = null;
            }
            
            if (user && typeof user === 'object' && user.business) {
              user.business.logo = base64Logo;
              kvSet('currentUser', user);
            }
          }
        }
        
        // Dispatch custom event to notify components of logo update
        publishBusinessLogoUpdated({ logo: base64Logo });
      } catch (error) {
        // Check if it's a quota exceeded error
        if (error.name === 'QuotaExceededError' || error.code === 22 || error.message?.includes('quota')) {
          console.error('❌ [LOGO] localStorage quota exceeded:', error);
          // Try to free up space by removing old logo first
          try {
            const storedBusiness = kvGet('currentBusiness');
            const isValidBusiness = storedBusiness && 
                                   storedBusiness !== '[object Object]' && 
                                   storedBusiness !== '"[object Object]"' && 
                                   (typeof storedBusiness !== 'string' || storedBusiness.trim() !== '');
            
            if (isValidBusiness) {
              let business;
              if (typeof storedBusiness === 'object' && storedBusiness !== null) {
                business = storedBusiness;
              } else if (typeof storedBusiness === 'string') {
                try {
                  business = JSON.parse(storedBusiness);
                } catch (parseError) {
                  console.warn('⚠️ [LOGO] Failed to parse currentBusiness in retry:', parseError);
                  business = { id: currentBusinessId };
                }
              } else {
                business = { id: currentBusinessId };
              }
              
              if (business && typeof business === 'object') {
                // Remove old logo to free space
                delete business.logo;
                kvSet('currentBusiness', business);
                
                // Try again with smaller logo
                business.logo = base64Logo;
                kvSet('currentBusiness', business);
              }
            }
          } catch (retryError) {
            // If still fails, return error
            return { 
              success: false, 
              error: 'Storage quota exceeded. Please try a smaller image or clear some browser data.' 
            };
          }
        } else {
          console.warn('Error updating local business logo:', error);
          // Continue with Supabase upload even if localStorage fails
          // The logo will still be available from Supabase
        }
      }

      // Try to upload to Supabase Storage
      let supabaseLogoUrl = null;
      try {
        const { supabase } = await import('../config/supabase');
        const fileExt = file.name.split('.').pop();
        const { resolveSupabaseBusinessId } = await import('../utils/businessTypeCloud');
        const logoBizId =
          resolveSupabaseBusinessId(currentBusinessId) || currentBusinessId || 'default';
        const fileName = `logo_${logoBizId}_${Date.now()}.${fileExt}`;
        const filePath = `business-logos/${fileName}`;

        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('business-assets')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.warn('Error uploading logo to Supabase Storage:', uploadError);
          // Continue with base64 only if Supabase fails
        } else {
          // Get public URL
          const { data: urlData } = supabase.storage
            .from('business-assets')
            .getPublicUrl(filePath);

          if (urlData?.publicUrl) {
            supabaseLogoUrl = urlData.publicUrl;
          }

          // Update business record in Supabase
          try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const logoToSave = supabaseLogoUrl || base64Logo;
              const { error: updateError } = await supabase
                .from('businesses')
                .update({ logo: logoToSave })
                .eq('auth_user_id', user.id);

              if (updateError) {
                console.warn('Error updating business logo in Supabase:', updateError);
              }
            }
          } catch (error) {
            console.warn('Error updating Supabase business record:', error);
          }
        }
      } catch (error) {
        console.warn('Supabase not available, using local storage only:', error);
      }

      // Clear cache to force refresh
      this.clearCache();

      return {
        success: true,
        logoUrl: supabaseLogoUrl || base64Logo,
        logoType: supabaseLogoUrl ? 'url' : 'base64'
      };
    } catch (error) {
      console.error('Error uploading logo:', error);
      return { success: false, error: error.message || 'Failed to upload logo' };
    }
  }

  /**
   * Compress and resize image before converting to base64
   * @param {File} file - The image file to compress
   * @param {number} maxWidth - Maximum width in pixels (default: 500)
   * @param {number} maxHeight - Maximum height in pixels (default: 500)
   * @param {number} quality - JPEG quality 0-1 (default: 0.8)
   * @returns {Promise<string>} Base64 string
   */
  async compressImage(file, maxWidth = 500, maxHeight = 500, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const mimeType = file.type || 'image/jpeg';
          const base64 = canvas.toDataURL(mimeType, quality);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert file to base64 string (with compression for images)
   * @param {File} file - The file to convert
   * @returns {Promise<string>} Base64 string
   */
  async fileToBase64(file) {
    // If it's an image, compress it first
    if (file.type && file.type.startsWith('image/')) {
      try {
        return await this.compressImage(file);
      } catch (error) {
        console.warn('Error compressing image, falling back to direct conversion:', error);
        // Fallback to direct conversion if compression fails
      }
    }
    
    // For non-images or if compression fails, use direct conversion
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get current business ID from localStorage
   * Delegates to sharedCore.getCurrentBusinessId() — single source of truth.
   * @returns {string|null}
   */
  getCurrentBusinessId() {
    return getCurrentBusinessId({ allowFallback: false }) || null;
  }

  /**
   * Delete logo (remove from local storage and optionally from Supabase)
   * @param {string} businessId - The business ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteLogo(businessId = null) {
    try {
      const currentBusinessId = businessId || this.getCurrentBusinessId();

      // Remove from local storage - update both currentBusiness and currentUser.business
      try {
        // Update currentBusiness
        const storedBusiness = kvGet('currentBusiness');
        if (storedBusiness) {
          // Handle corrupted data
          const isCorrupted = storedBusiness === '[object Object]' || 
                             storedBusiness === '"[object Object]"' || 
                             (typeof storedBusiness === 'string' && storedBusiness.trim() === '');
          
          if (isCorrupted) {
            console.warn('⚠️ [LOGO DELETE] Corrupted currentBusiness data, cleaning up');
            kvRemove('currentBusiness');
          } else {
            let business;
            if (typeof storedBusiness === 'object' && storedBusiness !== null) {
              business = storedBusiness;
            } else if (typeof storedBusiness === 'string') {
              try {
                business = JSON.parse(storedBusiness);
              } catch (parseError) {
                console.warn('⚠️ [LOGO DELETE] Failed to parse currentBusiness:', parseError);
                kvRemove('currentBusiness');
                business = null;
              }
            } else {
              business = null;
            }
            
            if (business && typeof business === 'object') {
              delete business.logo;
              kvSet('currentBusiness', business);
            }
          }
        }
        
        // Also update currentUser.business
        const currentUser = kvGet('currentUser');
        if (currentUser) {
          // Handle corrupted data
          const isUserCorrupted = currentUser === '[object Object]' || 
                                  currentUser === '"[object Object]"' || 
                                  (typeof currentUser === 'string' && currentUser.trim() === '');
          
          if (isUserCorrupted) {
            console.warn('⚠️ [LOGO DELETE] Corrupted currentUser data, skipping update');
          } else {
            let user;
            if (typeof currentUser === 'object' && currentUser !== null) {
              user = currentUser;
            } else if (typeof currentUser === 'string') {
              try {
                user = JSON.parse(currentUser);
              } catch (parseError) {
                console.warn('⚠️ [LOGO DELETE] Failed to parse currentUser:', parseError);
                user = null;
              }
            } else {
              user = null;
            }
            
            if (user && typeof user === 'object' && user.business) {
              delete user.business.logo;
              kvSet('currentUser', user);
            }
          }
        }
        
        // Dispatch custom event to notify components of logo deletion
        publishBusinessLogoUpdated({ logo: null });
      } catch (error) {
        console.warn('Error removing logo from local storage:', error);
      }

      // Try to remove from Supabase
      try {
        const { supabase } = await import('../config/supabase');
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: updateError } = await supabase
            .from('businesses')
            .update({ logo: null })
            .eq('auth_user_id', user.id);

          if (updateError) {
            console.warn('Error removing logo from Supabase:', updateError);
          }
        }
      } catch (error) {
        console.warn('Supabase not available:', error);
      }

      // Clear cache
      this.clearCache();

      return { success: true };
    } catch (error) {
      console.error('Error deleting logo:', error);
      return { success: false, error: error.message || 'Failed to delete logo' };
    }
  }
}

// Export singleton instance
export const businessInfoService = new BusinessInfoService();
export default businessInfoService;
